import { TerrainMaterial } from '../material';
import { Scene } from './Scene';
import { TerrainRef, TerrainSurfaceRegionRef } from './SceneManifest';
import { TiledTerrainStreamer } from './TiledTerrainStreamer';
import { TileMeshClient, TileMeshClientConfig, WorkerLike } from './terrain/TileMeshClient';
import {
  SurfaceTextureManager,
  SurfaceUrlResolver,
} from './terrain/SurfaceTextureManager';

/**
 * Construct a {@link SurfaceTextureManager} + {@link TerrainMaterial} for the
 * runtime / published flow. The url resolver points at `surfaces/{id}/{ch}.png`
 * (relative to the asset base URL configured on the resource loader). Editor
 * callers should instead build their own manager with an `/api/...` resolver,
 * then pass it into {@link loadTiledTerrain} via the explicit-manager overload.
 */
export function createRuntimeSurfaceManager(
  scene: Scene,
  ref: TerrainRef,
  options: { extraCapacity?: number } = {},
): { manager: SurfaceTextureManager; material: TerrainMaterial } {
  const urlResolver: SurfaceUrlResolver = (surfaceId, channel) =>
    `surfaces/${surfaceId}/${channel}.png`;
  const manager = new SurfaceTextureManager(scene.ctx, ref.surfaces, {
    urlResolver,
    extraCapacity: options.extraCapacity,
  });
  const material = buildMaterial(scene.ctx.gl, manager);
  return { manager, material };
}

/** Wires a fresh {@link TerrainMaterial} to a [SurfaceTextureManager]'s array textures. */
export function buildMaterial(
  gl: WebGL2RenderingContext,
  manager: SurfaceTextureManager,
): TerrainMaterial {
  const material = new TerrainMaterial(
    manager.albedoArray,
    manager.surfacePropsTex,
    manager.surfaceCount,
    gl,
  );
  material.normalArray = manager.normalArray;
  material.roughnessArray = manager.roughnessArray;
  return material;
}

/**
 * Load a tiled terrain from the manifest's `terrain` block. Constructs the
 * {@link SurfaceTextureManager} (runtime URL resolver), builds the shared
 * {@link TerrainMaterial}, fetches the heightmap PNG, and blocks on the LOD0
 * ring around (spawnX, spawnZ). The returned streamer must be ticked from
 * `Scene`'s per-frame hook so the ring follows the camera.
 *
 * Callers that need to inject a custom surface manager (e.g. the asset-manager
 * scene editor, which wants live `/api/projects/.../surfaces/...` URLs and
 * extra layer capacity for surfaces painted mid-session) should construct the
 * manager themselves and pass it via the `surfaceManager` option.
 */
export async function loadTiledTerrain(
  scene: Scene,
  ref: TerrainRef,
  spawnX: number,
  spawnZ: number,
  options: {
    terrainGenWorker?: WorkerLike;
    /** Pre-built manager + material — when provided, the runtime ones are NOT created. */
    surfaceManager?: SurfaceTextureManager;
    sharedMaterial?: TerrainMaterial;
  } = {},
): Promise<TiledTerrainStreamer> {
  let surfaceManager: SurfaceTextureManager;
  let sharedMaterial: TerrainMaterial;
  if (options.surfaceManager && options.sharedMaterial) {
    surfaceManager = options.surfaceManager;
    sharedMaterial = options.sharedMaterial;
  } else if (options.surfaceManager || options.sharedMaterial) {
    throw new Error('loadTiledTerrain: pass both surfaceManager AND sharedMaterial, or neither');
  } else {
    const built = createRuntimeSurfaceManager(scene, ref);
    surfaceManager = built.manager;
    sharedMaterial = built.material;
  }

  let heightmapGrid: { data: Uint8Array; width: number; height: number } | null = null;
  if (ref.heightmapPath) {
    try {
      const bytes = await scene.ctx.resourceLoader.loadResource(ref.heightmapPath);
      heightmapGrid = await scene.ctx.resourceLoader.loadImageData(bytes, 'image/png');
    } catch (e) {
      console.warn('Failed to load terrain heightmap PNG:', e);
    }
  }

  const worldWidth = ref.worldWidth ?? -2 * ref.worldMinX;
  const worldDepth = ref.worldDepth ?? -2 * ref.worldMinZ;
  const heightmapCellSize = ref.heightmapCellSize ?? 0;
  const heightmapAmplitude = ref.heightmapAmplitude ?? 0;
  const seed = ref.seed ?? 0;
  const surfaceRegions: TerrainSurfaceRegionRef[] = ref.surfaceRegions ?? [];
  const defaultSurfaceId = ref.defaultSurfaceId;

  const resolution =
    heightmapCellSize > 0 ? Math.max(worldWidth, worldDepth) / heightmapCellSize : 64;

  const meshClient = new TileMeshClient();
  const clientInit: TileMeshClientConfig = {
    width: worldWidth,
    depth: worldDepth,
    resolution,
    seed: String(seed),
    source: heightmapGrid
      ? {
          kind: 'heightmap',
          pixels: heightmapGrid.data,
          width: heightmapGrid.width,
          height: heightmapGrid.height,
          amplitude: heightmapAmplitude,
          worldMinX: ref.worldMinX,
          worldMinZ: ref.worldMinZ,
          worldWidth,
          worldDepth,
        }
      : {
          kind: 'noise',
          octaves: ref.noiseOctaves ?? 3,
          baseFrequency: ref.noiseBaseFrequency ?? 1,
          amplitude: heightmapAmplitude || 4,
          falloffShape: ref.noiseFalloffShape ?? 'none',
          worldWidth,
          worldDepth,
        },
  };
  meshClient.init(clientInit);
  if (options.terrainGenWorker) {
    meshClient.useWorker(options.terrainGenWorker);
  }

  const streamer = new TiledTerrainStreamer(
    scene,
    meshClient,
    ref.tileWorldSize,
    ref.worldMinX,
    ref.worldMinZ,
    worldWidth,
    worldDepth,
    ref.tilesX,
    ref.tilesZ,
    ref.maxLodLevel ?? 0,
    surfaceManager,
    sharedMaterial,
    heightmapGrid,
    heightmapAmplitude,
    heightmapCellSize,
    surfaceRegions,
    defaultSurfaceId,
  );
  await streamer.loadInitialTiles(spawnX, spawnZ);
  return streamer;
}
